import { useState } from "react";
import { Activity } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Chip } from "@/components/ui/chip";
import { Kpi } from "@/components/ui/kpi";
import { SkeletonRows } from "@/components/ui/skeleton";
import { date, inr, num, relativeTime } from "@/lib/format";

import {
  errorText,
  usePlatformDashboard,
  type PlatformDay,
  type TenantMetrics,
} from "./api";

/**
 * Platform dashboard (super tenant): how much each lender uses GraviCollect,
 * what it collects, and what needs attention. Aggregates only — the API sends
 * no names, contacts or records. "All lenders" sums every readable lender;
 * picking one shows just that lender.
 */
const RANGES = [7, 30, 90] as const;

const FEATURE_LABELS: Record<string, string> = {
  fieldDispositions: "Field dispositions",
  teleDispositions: "Telecalling dispositions",
  manualDispositions: "Manual dispositions",
  ptps: "PTPs captured",
  visits: "Visits",
  trips: "Trips started",
  weeklyPlans: "Weekly plans",
  cashReceipts: "Cash receipts",
  deposits: "Cash deposits",
  paymentLinks: "Payment links / QR",
  paymentsPaid: "Digital payments paid",
  settlements: "Settlement files",
  uploads: "File uploads",
  reconResolved: "Recon findings resolved",
  smsSent: "SMS sent",
  emailsSent: "Emails sent",
};

const C = {
  navy: "#204887",
  mid: "#4A77BD",
  cyan: "#0EA5B7",
  gold: "#B57B0B",
  teal: "#14855B",
  grid: "#E2E8F0",
};

export function PlatformDashboardPage() {
  const [days, setDays] = useState<number>(30);
  const [slug, setSlug] = useState<string>("");
  const dash = usePlatformDashboard(days);

  const all = dash.data?.tenants ?? [];
  const readable = all.filter((t) => t.readable);
  const selected = slug ? readable.filter((t) => t.slug === slug) : readable;
  const view = combine(selected);
  const maxSchema = Math.max(
    0,
    ...readable.map((t) => t.health?.schemaVersion ?? 0),
  );

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
            Platform
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
            <Activity className="h-5 w-5" /> Dashboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Adoption, usage, collections and health across lenders. Counts and
            totals only — no lender records.
            {dash.data && (
              <>
                {" "}
                {date(dash.data.from)} – {date(dash.data.to)} · updated{" "}
                {relativeTime(dash.data.generatedAt)}
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Lender"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900"
          >
            <option value="">All lenders</option>
            {readable.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.displayName}
              </option>
            ))}
          </select>
          {RANGES.map((r) => (
            <Chip key={r} active={days === r} onClick={() => setDays(r)}>
              {r} days
            </Chip>
          ))}
        </div>
      </div>

      {dash.isPending ? (
        <Card>
          <CardContent className="p-5">
            <SkeletonRows rows={6} />
          </CardContent>
        </Card>
      ) : dash.isError ? (
        <Card>
          <CardContent className="p-5 text-sm text-negative">
            Couldn't load the dashboard:{" "}
            {errorText(dash.error, "the server gave no reason.")}
          </CardContent>
        </Card>
      ) : (
        <>
          <Kpis view={view} lenders={all} selected={slug !== ""} days={days} />

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="Active users per day"
              description="Anyone who did anything signed in, including the app refreshing its session."
            >
              <LineChart data={view.daily}>
                {axes()}
                <Line
                  type="monotone"
                  dataKey="activeUsers"
                  name="Active users"
                  stroke={C.navy}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartCard>
            <ChartCard
              title="Sign-ins per day"
              description="Password sign-ins on the web and in the app."
            >
              <BarChart data={view.daily}>
                {axes()}
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="webLogins" name="Web" stackId="s" fill={C.navy} />
                <Bar
                  dataKey="mobileLogins"
                  name="Mobile"
                  stackId="s"
                  fill={C.cyan}
                />
              </BarChart>
            </ChartCard>
            <ChartCard
              title="Collected per day"
              description="Field cash and cheques (not cancelled) and successful digital payments."
            >
              <BarChart data={view.daily}>
                {axes(true)}
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="cashCollected"
                  name="Field"
                  stackId="c"
                  fill={C.navy}
                />
                <Bar
                  dataKey="digitalCollected"
                  name="Digital"
                  stackId="c"
                  fill={C.teal}
                />
              </BarChart>
            </ChartCard>
            <ChartCard
              title="Work recorded per day"
              description="Dispositions, visits and promises to pay."
            >
              <LineChart data={view.daily}>
                {axes()}
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="dispositions"
                  name="Dispositions"
                  stroke={C.navy}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="visits"
                  name="Visits"
                  stroke={C.cyan}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="ptps"
                  name="PTPs"
                  stroke={C.gold}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartCard>
          </div>

          <Attention lenders={all} maxSchema={maxSchema} only={slug} />

          {slug === "" && (
            <LenderTable
              lenders={all}
              maxSchema={maxSchema}
              onPick={(s) => setSlug(s)}
            />
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <Features view={view} lenders={selected} all={slug === ""} />
            <Builds builds={view.appBuilds} />
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

type View = ReturnType<typeof combine>;

/** Sum the chosen lenders into one picture. */
function combine(ts: TenantMetrics[]) {
  const sum = (f: (t: TenantMetrics) => number | null | undefined) =>
    ts.reduce((a, t) => a + Number(f(t) ?? 0), 0);
  const daily = new Map<string, PlatformDay>();
  for (const t of ts) {
    for (const d of t.daily) {
      const acc = daily.get(d.date) ?? {
        date: d.date,
        activeUsers: 0,
        webLogins: 0,
        mobileLogins: 0,
        cashCollected: 0,
        digitalCollected: 0,
        dispositions: 0,
        visits: 0,
        ptps: 0,
      };
      acc.activeUsers += d.activeUsers;
      acc.webLogins += d.webLogins;
      acc.mobileLogins += d.mobileLogins;
      acc.cashCollected += Number(d.cashCollected);
      acc.digitalCollected += Number(d.digitalCollected);
      acc.dispositions += d.dispositions;
      acc.visits += d.visits;
      acc.ptps += d.ptps;
      daily.set(d.date, acc);
    }
  }
  const features: Record<string, number> = {};
  const usingFeature: Record<string, number> = {};
  for (const t of ts) {
    for (const [k, v] of Object.entries(t.features ?? {})) {
      features[k] = (features[k] ?? 0) + v;
      if (v > 0) usingFeature[k] = (usingFeature[k] ?? 0) + 1;
    }
  }
  const appBuilds: Record<string, number> = {};
  for (const t of ts) {
    for (const [k, v] of Object.entries(t.adoption?.appBuilds ?? {})) {
      appBuilds[k] = (appBuilds[k] ?? 0) + v;
    }
  }
  return {
    users: sum((t) => t.adoption?.users),
    activeToday: sum((t) => t.adoption?.activeToday),
    active7d: sum((t) => t.adoption?.active7d),
    active30d: sum((t) => t.adoption?.active30d),
    neverSignedIn: sum((t) => t.adoption?.neverSignedIn),
    totpEnrolled: sum((t) => t.adoption?.totpEnrolled),
    webUsers: sum((t) => t.adoption?.webUsers),
    mobileUsers: sum((t) => t.adoption?.mobileUsers),
    newUsers: sum((t) => t.adoption?.newUsers),
    cash: sum((t) => t.business?.cashCollected),
    digital: sum((t) => t.business?.digitalCollected),
    cashReceipts: sum((t) => t.business?.cashReceipts),
    digitalPayments: sum((t) => t.business?.digitalPayments),
    cycleDue: sum((t) => t.business?.cycleDue),
    cyclePaid: sum((t) => t.business?.cyclePaidAmount),
    cycleCases: sum((t) => t.business?.cycleCases),
    cyclePaidCases: sum((t) => t.business?.cyclePaidCases),
    openCases: sum((t) => t.business?.openCases),
    outstanding: sum((t) => t.business?.outstanding),
    ptpsKept: sum((t) => t.business?.ptpsKept),
    ptpsResolved: sum((t) => t.business?.ptpsResolved),
    loans: sum((t) => t.business?.loans),
    newLoans: sum((t) => t.business?.newLoans),
    daily: [...daily.values()].sort((a, b) => a.date.localeCompare(b.date)),
    features,
    usingFeature,
    appBuilds,
  };
}

/** "42%", "0.8%" below 10, or "—" when there is nothing to divide by. */
function pct(part: number, whole: number): string {
  if (!whole) return "—";
  const v = (part / whole) * 100;
  return `${v > 0 && v < 10 ? v.toFixed(1) : Math.round(v)}%`;
}

function Kpis({
  view,
  lenders,
  selected,
  days,
}: {
  view: View;
  lenders: TenantMetrics[];
  selected: boolean;
  days: number;
}) {
  const collected = view.cash + view.digital;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {!selected && (
        <Kpi
          label="Lenders"
          value={num(lenders.filter((t) => t.status === "ACTIVE").length)}
          hint={`active of ${num(lenders.length)} · ${num(lenders.filter((t) => t.status === "FROZEN").length)} disabled`}
        />
      )}
      <Kpi
        label="Active users · 30d"
        value={num(view.active30d)}
        hint={`${pct(view.active30d, view.users)} of ${num(view.users)} users · ${num(view.activeToday)} today · ${num(view.active7d)} this week`}
      />
      <Kpi
        label={`Collected · ${days}d`}
        value={inr(collected)}
        hint={`${pct(view.digital, collected)} digital · ${num(view.cashReceipts)} receipts · ${num(view.digitalPayments)} payments`}
      />
      <Kpi
        label="This cycle · paid"
        value={pct(view.cyclePaid, view.cycleDue)}
        hint={`${inr(view.cyclePaid)} of ${inr(view.cycleDue)} · ${num(view.cyclePaidCases)}/${num(view.cycleCases)} cases`}
      />
      <Kpi
        label="Never signed in"
        value={num(view.neverSignedIn)}
        deltaSense="lower-is-better"
        hint={`${num(view.newUsers)} users added in ${days}d`}
      />
      <Kpi
        label={`Channels · ${days}d`}
        value={`${num(view.mobileUsers)} app`}
        hint={`${num(view.webUsers)} signed in on the web`}
      />
      <Kpi
        label={`PTPs kept · ${days}d`}
        value={pct(view.ptpsKept, view.ptpsResolved)}
        hint={`${num(view.ptpsKept)} kept of ${num(view.ptpsResolved)} resolved`}
      />
      <Kpi
        label="Open cases"
        value={num(view.openCases)}
        hint={`${inr(view.outstanding)} · ${num(view.loans)} loans, +${num(view.newLoans)} new`}
      />
      {selected && (
        <Kpi
          label="Authenticator"
          value={pct(view.totpEnrolled, view.users)}
          hint={`${num(view.totpEnrolled)} of ${num(view.users)} users set up 2FA`}
        />
      )}
    </div>
  );
}

function axes(money = false) {
  return [
    <CartesianGrid key="g" stroke={C.grid} vertical={false} />,
    <XAxis
      key="x"
      dataKey="date"
      tickFormatter={(d: string) => d.slice(8, 10) + "/" + d.slice(5, 7)}
      tick={{ fontSize: 11 }}
      minTickGap={16}
    />,
    <YAxis
      key="y"
      width={money ? 56 : 36}
      tick={{ fontSize: 11 }}
      allowDecimals={false}
      tickFormatter={(v: number) => (money ? inr(v) : num(v))}
    />,
    <Tooltip
      key="t"
      labelFormatter={(d: string) => date(d)}
      formatter={(v: number, name: string) => [money ? inr(v) : num(v), name]}
      contentStyle={{ fontSize: 12 }}
    />,
  ];
}

function ChartCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactElement;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

interface Issue {
  lender: string;
  text: string;
  tone: "negative" | "warning" | "info";
}

/** What needs someone, worst first. */
function issuesOf(t: TenantMetrics, maxSchema: number): Issue[] {
  const out: Issue[] = [];
  const add = (text: string, tone: Issue["tone"]) =>
    out.push({ lender: t.displayName, text, tone });
  if (t.status === "FROZEN") add("Disabled", "info");
  if (!t.readable) {
    if (t.status !== "PROVISIONING")
      add("Its data could not be read", "negative");
    else add("Still provisioning", "warning");
    return out;
  }
  const h = t.health!;
  if (h.schemaVersion != null && h.schemaVersion < maxSchema)
    add(`Database at V${h.schemaVersion}, others at V${maxSchema}`, "negative");
  if (h.duesAsOf == null) add("No dues file loaded yet", "warning");
  else if ((h.duesAgeDays ?? 0) > 2)
    add(
      `Dues file is ${h.duesAgeDays} days old (${date(h.duesAsOf)})`,
      "warning",
    );
  if (h.agentsDepositOverdue > 0)
    add(
      `${num(h.agentsDepositOverdue)} agents past their cash deposit deadline`,
      "negative",
    );
  if (h.failedUploads > 0)
    add(`${num(h.failedUploads)} failed uploads`, "warning");
  if (h.deadNotifications > 0)
    add(
      `${num(h.deadNotifications)} SMS/emails gave up after retries`,
      "warning",
    );
  if (h.failedPayments > 0)
    add(`${num(h.failedPayments)} failed digital payments`, "warning");
  if (h.lotsAwaitingConfirmation > 0)
    add(
      `${num(h.lotsAwaitingConfirmation)} deposits awaiting confirmation`,
      "info",
    );
  if (h.openReconFindings > 0)
    add(`${num(h.openReconFindings)} open reconciliation findings`, "info");
  return out;
}

const TONE_ORDER = { negative: 0, warning: 1, info: 2 } as const;

function Attention({
  lenders,
  maxSchema,
  only,
}: {
  lenders: TenantMetrics[];
  maxSchema: number;
  only: string;
}) {
  const issues = lenders
    .filter((t) => !only || t.slug === only)
    .flatMap((t) => issuesOf(t, maxSchema))
    .sort((a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Needs attention</CardTitle>
        <CardDescription>
          Problems are current except failed uploads and payments, which are for
          the chosen window.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {issues.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-muted-foreground">
            Nothing needs attention.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {issues.map((i, n) => (
              <li
                key={n}
                className="flex items-center justify-between gap-3 px-5 py-2 text-sm"
              >
                <span>
                  <span className="font-medium text-navy-900">{i.lender}</span>
                  <span className="text-muted-foreground"> · {i.text}</span>
                </span>
                <Badge tone={i.tone}>
                  {i.tone === "negative"
                    ? "Act now"
                    : i.tone === "warning"
                      ? "Check"
                      : "FYI"}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function LenderTable({
  lenders,
  maxSchema,
  onPick,
}: {
  lenders: TenantMetrics[];
  maxSchema: number;
  onPick: (slug: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lenders</CardTitle>
        <CardDescription>Pick one to see only its numbers.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-2 font-semibold">Lender</th>
                <th className="px-3 py-2 text-right font-semibold">Users</th>
                <th className="px-3 py-2 text-right font-semibold">
                  Active 30d
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  App users
                </th>
                <th className="px-3 py-2 text-right font-semibold">2FA</th>
                <th className="px-3 py-2 text-right font-semibold">
                  Collected
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  Cycle paid
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  PTPs kept
                </th>
                <th className="px-3 py-2 font-semibold">Dues file</th>
                <th className="px-5 py-2 text-right font-semibold">Issues</th>
              </tr>
            </thead>
            <tbody>
              {lenders.map((t) => {
                const a = t.adoption;
                const b = t.business;
                const issues = issuesOf(t, maxSchema).filter(
                  (i) => i.tone !== "info",
                ).length;
                return (
                  <tr
                    key={t.slug}
                    onClick={() => t.readable && onPick(t.slug)}
                    className={`border-b border-slate-100 last:border-0 ${t.readable ? "cursor-pointer hover:bg-navy-50" : ""}`}
                  >
                    <td className="px-5 py-2.5">
                      <div className="font-medium text-navy-900">
                        {t.displayName}
                      </div>
                      <div className="font-mono text-2xs text-muted-foreground">
                        {t.slug} · {t.tier}
                        {t.status !== "ACTIVE" &&
                          ` · ${t.status.toLowerCase()}`}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {num(a?.users)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {a
                        ? `${num(a.active30d)} · ${pct(a.active30d, a.users)}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {num(a?.mobileUsers)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {a ? pct(a.totpEnrolled, a.users) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {b
                        ? inr(
                            Number(b.cashCollected) +
                              Number(b.digitalCollected),
                          )
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {b
                        ? pct(Number(b.cyclePaidAmount), Number(b.cycleDue))
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {b ? pct(b.ptpsKept, b.ptpsResolved) : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {t.health?.duesAsOf ? date(t.health.duesAsOf) : "—"}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      {issues > 0 ? (
                        <Badge tone="warning">{issues}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Features({
  view,
  lenders,
  all,
}: {
  view: View;
  lenders: TenantMetrics[];
  all: boolean;
}) {
  const keys = Object.keys(FEATURE_LABELS);
  const top = Math.max(1, ...keys.map((k) => view.features[k] ?? 0));
  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle>Features used</CardTitle>
        <CardDescription>
          How often each part of the product was used in the window
          {all ? ", and by how many lenders." : "."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5">
          {keys.map((k) => {
            const v = view.features[k] ?? 0;
            return (
              <li
                key={k}
                className="grid grid-cols-[11rem_1fr_auto] items-center gap-3 text-sm"
              >
                <span className="truncate text-navy-900">
                  {FEATURE_LABELS[k]}
                </span>
                <span className="h-2 rounded bg-slate-100">
                  <span
                    className="block h-2 rounded bg-navy-600"
                    style={{ width: `${(v / top) * 100}%` }}
                  />
                </span>
                <span className="w-28 text-right tabular-nums text-muted-foreground">
                  {num(v)}
                  {all &&
                    ` · ${num(view.usingFeature[k] ?? 0)}/${num(lenders.length)}`}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function Builds({ builds }: { builds: Record<string, number> }) {
  const rows = Object.entries(builds).sort((a, b) => b[1] - a[1]);
  const total = rows.reduce((a, [, n]) => a + n, 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle>App builds</CardTitle>
        <CardDescription>
          Each field agent's build on their latest trip in the last 30 days.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No trips yet.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {rows.map(([build, n]) => (
              <li key={build} className="flex justify-between gap-3">
                <span className="font-mono text-navy-900">{build}</span>
                <span className="tabular-nums text-muted-foreground">
                  {num(n)} · {pct(n, total)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
